import * as React from 'react';
import { Typography, Stack, Box, Button, Grid, Dialog, DialogContent, Stepper, Step, StepLabel, useMediaQuery, Tooltip } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@mui/styles';
import CloseModalIcon from '../../../icons/RoundCloseIcon.svg';
import BankInputBox from '../../../shared/BankInput';
import AddBankBgImage from '../../../icons/addBankBgImage.png';
import AddBankIcon from '../../../icons/AddBankIcon.png';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { getCreatingBankAccount, getResetBankAccountsState, getUpdatingBankAccount, selectBankAccountsState } from '../../../shared/sagas/bankAccounts/bankAccountsSlice';
import SpinnerModal from '../../../shared/Modal/SpinnerModal';
import { approvedISOStandards } from '../../../shared/util/approvedISOStandards';
import { BankRoutingNumberRegex, NumberRegex } from '../../../utils/regex';
import { BankAccountDTO } from '@inosio/payments-sdk';
import { CustomDropZone } from '../../onboarding/components/CustomDropZone';
import HelpIcon from '@mui/icons-material/Help';
import { uploadDocuments, selectDocumentState, resetDocumentsState } from '../../../shared/sagas/document/documentSlice';

interface CreateUpdateBankAccountModalProps {
  open: boolean;
  data?: BankAccountDTO;
  isUpdateModal?: boolean;
  searchDataCallback?: () => void;
  closeCallback: () => void;
}

const useStyles = makeStyles((theme) => ({
  wrapper: {
    margin: '1px',
    position: 'relative',
  },
  stepperContainer: {
    paddingTop: '50px',
    width: '100%',
  },
  stepper: {
    width: '100%',
    '& .MuiStepLabel-root': {
      display: 'flex',
      flexDirection: 'column-reverse !important',
      marginTop: '-65px',
    },
    '& .MuiStepLabel-label': {
      display: 'flex',
      justifyContent: 'center',
    },
    '& .MuiStepLabel-labelContainer': {
      marginBottom: '9px',
    },
    '& .MuiStepConnector-root': {
      top: '15px',
    },
    '& .MuiStepIcon-root': {
      color: '#F1F0F5',
      width: '29px',
      height: '29px',
    },
    '& .MuiStepIcon-root.Mui-completed': {
      color: '#39DB8D',
    },
    '& .MuiStepIcon-root .MuiStepIcon-text': {
      fill: '#14142B',
      fontWeight: '600',
    },
    '& .MuiStepIcon-root.Mui-active': {
      color: '#39DB8D',
    },
    '& .MuiStepIcon-root.Mui-active .MuiStepIcon-text': {
      fill: '#fff',
    },
  },
  stepperText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: '18px !important',
    lineHeight: '21px !important',
    maxWidth: '120px',
    color: '#14142B',
  },
}));

const steps = ['bank account', 'bank letter\u00a0/\u00a0check'];

export default function CreateUpdateBankAccountModal(props: CreateUpdateBankAccountModalProps) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    createdBankAccount,
    isCreatingBankAccount,
    isCreatingBankAccountSuccess,
    isCreatingBankAccountFailed,
    isUpdatingBankAccount,
    isUpdatingBankAccountSuccess,
    isUpdatingBankAccountFailed,
    errorMessage,
  } = useSelector(selectBankAccountsState);

  const [checkingActive, setCheckingActive] = React.useState(props.isUpdateModal ? props.data?.AccountType === 'C' : true);
  const [savingActive, setSavingActive] = React.useState(props.data?.AccountType === 'S');
  const [bankName, setBankName] = React.useState(props.data?.BankName || '');
  const [bankHolderName, setBankHolderName] = React.useState(props.isUpdateModal ? `${props.data?.AccountHolderFirstname || ''} ${props.data?.AccountHolderLastname || ''}`.trim() : '');
  const [bankAccountNumber, setBankAccountNumber] = React.useState(props.data?.AccountLastFour ? `****${props.data?.AccountLastFour}` : '');
  const [bankRoutingNumber, setBankRoutingNumber] = React.useState(props.data?.RoutingNumber || '');
  const [bankNickName, setBankNickName] = React.useState(props.data?.NickName || '');
  const [bankNameError, setBankNameError] = React.useState('');
  const [bankHolderNameError, setBankHolderNameError] = React.useState('');
  const [bankAccountNumberError, setBankAccountNumberError] = React.useState('');
  const [bankRoutingNumberError, setBankRoutingNumberError] = React.useState('');
  const [bankGlobalError, setBankGlobalError] = React.useState<string | null>(null);
  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = React.useState(false);
  const [spinnerStatus, setSpinnerStatus] = React.useState<string | null>(null);
  const [bankCountry, setBankCountry] = React.useState(props.data?.BillingAddress?.Country || 'US');
  const [bankAddress, setBankAddress] = React.useState(props.data?.BillingAddress?.Line1 || '');
  const [bankCity, setBankCity] = React.useState(props.data?.BillingAddress?.City || '');
  const [bankState, setBankState] = React.useState(props.data?.BillingAddress?.State || '');
  const [bankZip, setBankZip] = React.useState(props.data?.BillingAddress?.Zip || '');
  const [bankAddressError, setBankAddressError] = React.useState('');
  const [bankZipError, setBankZipError] = React.useState('');
  const [bankCountryError, setBankCountryError] = React.useState('');
  const [bankCityError, setBankCityError] = React.useState('');
  const [bankStateError, setBankStateError] = React.useState('');
  const { Countries: approvedCountries, States: approvedStates, Provinces: approvedProvinces } = approvedISOStandards;
  const isOnMobileScreen = useMediaQuery('(max-width:600px)');

  const validateBankAddress = () => {
    if (bankAddress.length === 0) {
      setBankAddressError('Bank address is empty!');
      return false;
    } else {
      setBankAddressError('');
      return true;
    }
  };
  const validateBankZip = () => {
    if (bankZip.length === 0) {
      setBankZipError('Bank ZIP code is empty!');
      return false;
    } else {
      setBankZipError('');
      return true;
    }
  };
  const validateBankCountry = () => {
    if (bankCountry.length === 0) {
      setBankCountryError('Bank country is empty!');
      return false;
    } else {
      setBankCountryError('');
      return true;
    }
  };
  const validateBankCity = () => {
    if (bankCity.length === 0) {
      setBankCityError('Bank city is empty!');
      return false;
    } else {
      setBankCityError('');
      return true;
    }
  };
  const validateBankState = () => {
    if (bankState.length === 0) {
      setBankStateError('Bank state is empty!');
      return false;
    } else {
      setBankStateError('');
      return true;
    }
  };
  const validateBankName = () => {
    if (bankName.length === 0) {
      setBankNameError('Bank name is empty!');
      return false;
    } else {
      setBankNameError('');
      return true;
    }
  };
  const validateBankHolderName = () => {
    if (bankHolderName.length === 0) {
      setBankHolderNameError('Bank holder name is empty!');
      return false;
    } else {
      setBankHolderNameError('');
      return true;
    }
  };
  const validateBankAccountNumber = () => {
    if (bankAccountNumber.length === 0) {
      setBankAccountNumberError('Bank account number is empty!');
      return false;
    } else {
      setBankAccountNumberError('');
      return true;
    }
  };
  const validateBankRoutingNumber = () => {
    if (bankRoutingNumber.length === 0) {
      setBankRoutingNumberError('Bank routing number is empty!');
      return false;
    } else if (bankRoutingNumber.length !== 9) {
      setBankGlobalError('Routing number must be exactly 9 digits.');
      setBankRoutingNumberError('Routing number must be exactly 9 digits.');
      return false;
    } else if (!BankRoutingNumberRegex.test(bankRoutingNumber)) {
      setBankGlobalError('Routing number must start with a 0, 1, 2 or 3.');
      setBankRoutingNumberError('Routing number must start with a 0, 1, 2 or 3.');
      return false;
    } else {
      setBankRoutingNumberError('');
      return true;
    }
  };
  const onChangeAccountNumberHandler = (e: any) => {
    if (e.target.value === '' || NumberRegex.test(e.target.value)) {
      setBankAccountNumber(e.target.value);
    }
  };
  const onChangeRoutingNumberHandler = (e: any) => {
    if (e.target.value === '' || NumberRegex.test(e.target.value)) {
      setBankRoutingNumber(e.target.value);
    }
  };

  const [alignment, setAlignment] = React.useState('checking');

  const handleChange = (event: React.MouseEvent<HTMLElement>, newAlignment: string) => {
    setAlignment(newAlignment);
  };

  const [isValidForm, setIsValidForm] = React.useState(false);
  React.useEffect(() => {
    if (bankName && bankHolderName && bankAccountNumber && bankRoutingNumber && bankAddress && bankCountry && bankCity && bankState && bankZip) {
      setIsValidForm(true);
    } else {
      setIsValidForm(false);
    }
  }, [bankName, bankHolderName, bankAccountNumber, bankRoutingNumber, bankAddress, bankCountry, bankCity, bankState, bankZip]);

  const [isRefreshingPageNeeded, setIsRefreshingPageNeeded] = React.useState(false);
  const handleAddBankSubmit = () => {
    setBankGlobalError('');

    const names = bankHolderName.split(' ');

    if (
      validateBankName() &&
      validateBankHolderName() &&
      validateBankAccountNumber() &&
      validateBankRoutingNumber() &&
      validateBankAddress() &&
      validateBankCity() &&
      validateBankState() &&
      validateBankZip() &&
      validateBankCountry()
    ) {
      setIsSpinnerModalOpen(true);

      dispatch({
        type: props.isUpdateModal ? getUpdatingBankAccount.type : getCreatingBankAccount.type,
        payload: {
          //Savings: S, checkings: C
          AccountType: checkingActive ? 'C' : 'S',
          AccountNumber: bankAccountNumber,
          RoutingNumber: bankRoutingNumber,
          BillingAddress: {
            City: bankCity,
            State: bankState,
            Zip: bankZip,
            Line1: bankAddress,
            Country: bankCountry,
          },
          AccountHolderFirstname: names[0],
          AccountHolderLastname: names[1],
          NickName: bankNickName,
          BankName: bankName,
          Uuid: props.isUpdateModal ? props.data?.Uuid : undefined,
        },
      });

      setIsRefreshingPageNeeded(true);
    }
  };

  const handleCloseSpinnerModal = React.useCallback(() => {
    setIsSpinnerModalOpen(false);
  }, [dispatch]);

  React.useEffect(() => {
    if (isCreatingBankAccountSuccess || isUpdatingBankAccountSuccess) {
      if (activeStep === 0) {
        setSpinnerStatus('stop');
        setActiveStep(1);
      } else {
        handleCloseModal();
      }
    }
    if (isCreatingBankAccountFailed || isUpdatingBankAccountFailed) {
      if (activeStep === 0) {
        setIsSpinnerModalOpen(false);
        setBankGlobalError(errorMessage.message);
      } else {
        handleCloseModal();
      }
    }
  }, [isCreatingBankAccountSuccess, isCreatingBankAccountFailed, isUpdatingBankAccountSuccess, isUpdatingBankAccountFailed, errorMessage]);

  const handleCloseModal = () => {
    dispatch({ type: getResetBankAccountsState.type });
    dispatch({ type: resetDocumentsState.type });

    isRefreshingPageNeeded && props.searchDataCallback && props.searchDataCallback();
    props.closeCallback();
  };

  const [activeStep, setActiveStep] = React.useState(0);

  const [bankFiles, setBankFiles] = React.useState<Array<any>>(props.data?.BankFiles || []);
  const handleBankFiles = (acceptedFiles: File[]) => {
    let files = bankFiles;

    if (files.length + acceptedFiles.length <= 3) {
      acceptedFiles.forEach((af: File) => {
        if (!files.find((f) => f.name === af.name)) {
          files = [...files, af];
        }
      });
      setBankFiles(files);
    }
  };

  const triggerSubAccountsCreation = () => {
    dispatch({ type: getResetBankAccountsState.type });

    dispatch({
      type: getUpdatingBankAccount.type,
      payload: {
        DocumentSubmitted: true,
        Uuid: props.isUpdateModal ? props.data?.Uuid : createdBankAccount.Uuid,
      },
    });
  };

  const handleSaveBankFiles = () => {
    setBankGlobalError('');

    setIsSpinnerModalOpen(true);

    const uploadingFiles = bankFiles?.filter((f) => f.name);

    if (uploadingFiles.length > 0) {
      dispatch({
        type: uploadDocuments.type,
        payload: {
          belongsToEntityUuid: props.isUpdateModal ? props.data?.Uuid : createdBankAccount.Uuid,
          documents: uploadingFiles,
          documentType: 'BankAccountFile',
        },
      });
    } else triggerSubAccountsCreation();
  };

  const { isUploadingDocuments, isUploadingDocumentsSuccess, isUploadingDocumentsFailed, documentErrorMessage, uploadDocumentResponse: documentResponse } = useSelector(selectDocumentState);

  React.useEffect(() => {
    if (isUploadingDocumentsSuccess) {
      if (documentResponse?.some((d) => d.remoteUuid)) {
        triggerSubAccountsCreation();
      } else {
        setSpinnerStatus('stop');
        setBankGlobalError('Uploading new documents failed. Please try again later.');
      }
    }
    if (isUploadingDocumentsFailed) {
      setSpinnerStatus('stop');
      setBankGlobalError(documentErrorMessage.message);
    }
  }, [isUploadingDocumentsSuccess, isUploadingDocumentsFailed, documentErrorMessage]);

  const handleDeleteBankFiles = (file: File) => {
    setBankFiles(bankFiles.filter((f: File) => f.name !== file.name));
  };

  return (
    <React.Fragment>
      <Dialog
        open={props.open}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        fullScreen={isOnMobileScreen}
        fullWidth={true}
        disableEnforceFocus
        style={{
          background: 'none',
        }}
        PaperProps={{
          style: {
            pointerEvents: 'auto',
            width: '520px',
            minHeight: '555px',
            borderRadius: '24px',
          },
        }}
      >
        <DialogContent>
          <Box component="form" noValidate>
            <Grid container spacing={2} style={{ marginBottom: '5px' }}>
              <Grid item xs={1}></Grid>
              <Grid item xs={10}>
                <Box className={classes.stepperContainer} sx={{ display: isOnMobileScreen ? 'none' : 'block' }}>
                  <Stepper alternativeLabel activeStep={activeStep} classes={{ root: classes.stepper }}>
                    {steps.map((label) => (
                      <Step key={label}>
                        <StepLabel sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Typography className={classes.stepperText}>{label}</Typography>
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>
              </Grid>
              <Grid item xs={1}>
                <img
                  src={CloseModalIcon}
                  alt="close"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    handleCloseModal();
                    setBankHolderName('');
                    setBankName('');
                    setBankRoutingNumber('');
                    setBankNickName('');
                    setBankAccountNumber('');
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                {bankGlobalError?.length ? (
                  <Typography
                    style={{
                      fontFamily: "'Open Sans', sans-serif",
                      fontStyle: 'normal',
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '18px',
                      color: '#DB3939',
                      textAlign: 'center',
                      borderTop: '2px  solid #DB3939',
                      borderRadius: '24px',
                      padding: '12px 20px',
                      width: '100%',
                      margin: '0px auto',
                    }}
                  >
                    {bankGlobalError}
                  </Typography>
                ) : (
                  ''
                )}
              </Grid>
            </Grid>

            {activeStep === 0 ? (
              <React.Fragment>
                <Stack
                  direction="column"
                  justifyContent="space-between"
                  alignItems="space-between"
                  spacing={2}
                  style={{
                    backgroundImage: 'url(' + AddBankBgImage + ')',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '330px 211px',
                    height: '210px',
                    width: '330px',
                    margin: '0 auto',
                    padding: '16px',
                    marginBottom: '30px',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="space-between" spacing={1}>
                    <div>
                      <Typography
                        style={{
                          fontFamily: "'Open Sans', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 600,
                          fontSize: '14px',
                          lineHeight: ' 13px',
                          textTransform: 'capitalize',
                          color: '#14142B',
                        }}
                      >
                        Bank account
                      </Typography>
                    </div>
                    <div>
                      <img src={AddBankIcon} alt="card" style={{ width: '16px', height: '15px' }} />
                    </div>
                  </Stack>
                  <div style={{ marginBottom: '5px' }}>
                    <BankInputBox
                      label="Bank Name"
                      style={{ width: '200px' }}
                      name="bank-name"
                      placeholder=""
                      value={bankName}
                      onChange={(e: any) => setBankName(e.target.value)}
                      autoComplete="bank-name"
                      error={bankNameError}
                    />
                  </div>
                  <div style={{ marginBottom: '5px' }}>
                    <BankInputBox
                      label="Name of account holder"
                      style={{ width: '200px' }}
                      placeholder=""
                      name="bank-holder"
                      value={bankHolderName}
                      onChange={(e: any) => setBankHolderName(e.target.value)}
                      autoComplete="bank-holder"
                      error={bankHolderNameError}
                    />
                  </div>
                  <Stack direction="row" justifyContent="flex-start" alignItems="flex-start" spacing={1}>
                    <div style={{ marginBottom: '5px' }}>
                      <BankInputBox
                        label="Account Number"
                        style={{ width: '135px' }}
                        placeholder=""
                        name="account-number"
                        value={bankAccountNumber}
                        onChange={onChangeAccountNumberHandler}
                        autoComplete="account-number"
                        disabled={!!props.isUpdateModal}
                        error={bankAccountNumberError}
                      />
                    </div>
                    <div style={{ marginBottom: '5px' }}>
                      <BankInputBox
                        label="Routing Number"
                        style={{ width: '135px' }}
                        placeholder=""
                        name="routing-number"
                        value={bankRoutingNumber}
                        onChange={onChangeRoutingNumberHandler}
                        autoComplete="routing-number"
                        disabled={!!props.isUpdateModal}
                        error={bankRoutingNumberError}
                      />
                    </div>
                  </Stack>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  spacing={2}
                  style={{
                    marginBottom: '15px',
                  }}
                >
                  <input
                    placeholder="Nick name"
                    style={{
                      border: '1px solid #F1F0F5',
                      fontFamily: "'Open Sans', sans-serif",
                      borderRadius: '24px',
                      height: '36px',
                      width: '190px',
                      padding: '10px',
                    }}
                    name="nickname"
                    type="text"
                    value={bankNickName}
                    onChange={(e) => setBankNickName(e.target.value)}
                    autoComplete="nickname"
                  />
                  <ToggleButtonGroup color="primary" value={alignment} exclusive onChange={handleChange} style={{ width: '190px' }}>
                    <ToggleButton
                      value="checking"
                      style={{
                        fontFamily: "'Work Sans', sans-serif",
                        width: '50%',
                        border: '1px solid #39A2DB',
                        borderRadius: '24px 0 0 24px ',
                        background: checkingActive ? '#39A2DB' : '#fff',
                        color: checkingActive ? '#fff' : '#39A2DB',
                        textTransform: 'capitalize',
                        fontSize: '16px',
                        lineHeight: '22px',
                        padding: '5px 10px',
                        height: '36px',
                      }}
                      size="small"
                      onClick={() => {
                        setCheckingActive(true);
                        setSavingActive(false);
                      }}
                    >
                      Checking
                    </ToggleButton>
                    <ToggleButton
                      value="savings"
                      style={{
                        width: '50%',
                        fontFamily: "'Work Sans', sans-serif",
                        border: '1px solid #39A2DB',
                        borderRadius: '0 24px  24px 0 ',
                        background: savingActive ? '#39A2DB' : '#fff',
                        color: savingActive ? '#fff' : '#39A2DB',
                        textTransform: 'capitalize',
                        fontSize: '16px',
                        lineHeight: '22px',
                        padding: '5px 10px',
                        height: '36px',
                      }}
                      size="small"
                      onClick={() => {
                        setCheckingActive(false);
                        setSavingActive(true);
                      }}
                    >
                      Savings
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} style={{ marginBottom: '15px' }}>
                  <input
                    placeholder="Address"
                    style={{
                      border: '1px solid #F1F0F5',
                      borderRadius: '24px',
                      height: '36px',
                      width: '400px',
                      margin: '0 auto',
                      padding: '0 10px',
                      borderTop: bankAddressError.length ? '2px  solid #DB3939' : '1px solid #F1F0F5',
                      marginLeft: isOnMobileScreen ? '-15px' : '0px',
                      marginRight: isOnMobileScreen ? '-15px' : '0px',
                    }}
                    value={bankAddress}
                    onChange={(e) => setBankAddress(e.target.value)}
                    type="text"
                    name="address"
                  />
                </Stack>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} style={{ marginBottom: '15px' }}>
                  <input
                    placeholder="City"
                    name="city"
                    type="text"
                    style={{
                      border: '1px solid #F1F0F5',
                      borderRadius: '24px',
                      height: '36px',
                      width: '190px',
                      padding: '15px',
                      borderTop: bankCityError.length ? '2px  solid #DB3939' : '1px solid #F1F0F5',
                    }}
                    value={bankCity}
                    onChange={(e) => setBankCity(e.target.value)}
                  />
                  <div placeholder="State">
                    <select
                      placeholder="State"
                      name="state"
                      style={{
                        border: '1px solid #F1F0F5',
                        borderRadius: '24px',
                        height: '36px',
                        width: '190px',
                        padding: '0 15px',
                        borderTop: bankStateError.length ? '2px  solid #DB3939' : '1px solid #F1F0F5',
                      }}
                      defaultValue={bankState}
                      onChange={(e) => setBankState(e.target.value)}
                    >
                      <option style={{ height: '36px' }} value="">
                        {`Select State`}
                      </option>
                      {bankCountry === 'US' ? (
                        approvedStates.map((stateRecord) => (
                          <option style={{ height: '36px' }} key={stateRecord.Code} value={stateRecord.Code}>
                            {stateRecord.State + ' - ' + stateRecord.Code}
                          </option>
                        ))
                      ) : bankCountry === 'CA' ? (
                        approvedProvinces.map((provinceRecord) => (
                          <option style={{ height: '36px' }} key={provinceRecord.Code} value={provinceRecord.Code}>
                            {provinceRecord.Province + ' - ' + provinceRecord.Code}
                          </option>
                        ))
                      ) : (
                        <option style={{ height: '36px' }} value="NA">
                          other
                        </option>
                      )}
                    </select>
                  </div>
                </Stack>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} style={{ marginBottom: '15px' }}>
                  <input
                    placeholder="ZIP"
                    style={{
                      border: '1px solid #F1F0F5',
                      borderRadius: '24px',
                      height: '36px',
                      width: '190px',
                      padding: '0 15px',
                      borderTop: bankZipError.length ? '2px  solid #DB3939' : '1px solid #F1F0F5',
                    }}
                    name="zip"
                    type="text"
                    value={bankZip}
                    onChange={(e) => setBankZip(e.target.value)}
                  />
                  <div placeholder="Country">
                    <select
                      placeholder="Country"
                      name="country"
                      style={{
                        border: '1px solid #F1F0F5',
                        borderRadius: '24px',
                        height: '36px',
                        width: '190px',
                        padding: '0 15px',
                        borderTop: bankCountryError.length ? '2px  solid #DB3939' : '1px solid #F1F0F5',
                      }}
                      defaultValue="US"
                      onChange={(e) => {
                        setBankCountry(e.target.value);
                        setBankState('');
                      }}
                    >
                      {approvedCountries.map((countryRecord) => (
                        <option style={{ height: '36px' }} key={countryRecord.Code} value={countryRecord.Code}>
                          {countryRecord.Country + ' - ' + countryRecord.Code}
                        </option>
                      ))}
                    </select>
                  </div>
                </Stack>
                <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2} style={{ width: '400px', margin: '0 auto' }}>
                  <div className={classes.wrapper}>
                    <Button
                      variant="contained"
                      id="add-bank-btn"
                      sx={{ width: 100 }}
                      className={isCreatingBankAccount || isUpdatingBankAccount ? '' : 'active'} //it's for e2e test
                      disabled={Boolean(!!isCreatingBankAccount || !!isUpdatingBankAccount || !isValidForm)}
                      onClick={handleAddBankSubmit}
                    >
                      Continue
                    </Button>
                  </div>
                </Stack>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Typography
                  style={{
                    fontWeight: 700,
                    fontSize: '18px',
                    lineHeight: '25px',
                    paddingLeft: '0px',
                    paddingTop: '0px',
                    color: '#14142B',
                  }}
                  sx={{ p: 2 }}
                >
                  {`Upload void check or bank letter`}
                  <Tooltip title="Please provide a document which shows the name of the company or account owner and the full account and routing number." placement="right">
                    <HelpIcon sx={{ fontSize: 17, color: '#C4C4C4', cursor: 'pointer', ml: '5px' }} />
                  </Tooltip>
                </Typography>

                <Box sx={{ marginBottom: '24px' }}>
                  {/* maxFileSize must be in MB, example: maxFileSize={20} */}
                  <CustomDropZone
                    files={bankFiles}
                    fileLimit={3}
                    handleFilesAdded={handleBankFiles}
                    handleFileDeleted={handleDeleteBankFiles}
                    maxFileSize={20}
                    description="Up to 20MB per file. Up to 3 Files at a time."
                  />
                  <Typography style={{ fontFamily: 'Work Sans', fontSize: '14px', marginTop: '10px' }}>{'Supported File Types: .jpg, .pdf , .png'}</Typography>
                </Box>

                <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2} style={{ width: '400px', margin: '0 auto' }}>
                  <div className={classes.wrapper}>
                    <Button
                      variant="contained"
                      id="add-bank-btn"
                      sx={{ width: 100 }}
                      className={isUploadingDocuments ? '' : 'active'} //it's for e2e test
                      disabled={Boolean(!!isUploadingDocuments || bankFiles.length === 0)}
                      onClick={handleSaveBankFiles}
                    >
                      Save
                    </Button>
                  </div>
                </Stack>
              </React.Fragment>
            )}
          </Box>
        </DialogContent>
      </Dialog>
      {isSpinnerModalOpen ? (
        <SpinnerModal
          height={isOnMobileScreen ? '100%' : '620px'}
          width="520px"
          openSpinnerModal={isSpinnerModalOpen}
          handleCloseSpinnerModal={handleCloseSpinnerModal}
          spinnerStatus={spinnerStatus}
          closeCancel={() => setIsSpinnerModalOpen(false)}
          isInvisible={true}
        />
      ) : (
        <></>
      )}
    </React.Fragment>
  );
}
